import React from 'react';
import { MapPin, Plane } from 'lucide-react';
import PrivateRideInlineLocationField from '../private-rides/PrivateRideInlineLocationField';

function AirportPlaneIcon({ className = 'h-4 w-4' }) {
  // Exact Lucide Plane icon used in UniAirBookingTypeFlow.jsx.
  // Keeping this as the library component guarantees the same recognised aircraft shape.
  return <Plane aria-hidden="true" className={className} strokeWidth={2} />;
}

export default function AirportJourneyCard({
  tripType,
  selectedLocation,
  locationText,
  onSelectLocation,
  onLocationTextChange,
  onEditLocation,
  disabled = false,
}) {
  const isToAirport = tripType === 'dropoff';
  const editableLabel = isToAirport ? 'Pickup location' : 'Destination';
  const editablePlaceholder = isToAirport ? 'Set your exact pickup location' : 'Set your exact destination';
  const airportLabel = isToAirport
    ? 'Cape Town International Airport · Departures'
    : 'Cape Town International Airport · Arrivals';

  // These are render helpers, not nested React component types. Returning the
  // elements directly preserves the input DOM node and keyboard focus when
  // AirportBooking state updates on every character typed.
  const renderEditableEndpoint = () => (
    <div className="flex min-w-0 items-center gap-3 rounded-xl px-2 py-1">
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#720e1e]/10 text-[#720e1e]"
        aria-hidden="true"
      >
        <MapPin className="h-4 w-4" strokeWidth={2} />
      </span>
      <PrivateRideInlineLocationField
        id={isToAirport ? 'airport-inline-pickup' : 'airport-inline-destination'}
        title={editableLabel}
        placeholder={editablePlaceholder}
        point={selectedLocation}
        displayText={locationText}
        searchScope={isToAirport ? 'all' : 'market'}
        onSelect={onSelectLocation}
        onTextChange={onLocationTextChange}
        onMapPin={onEditLocation}
        disabled={disabled}
      />
    </div>
  );

  const renderAirportEndpoint = () => (
    <div className="flex items-center gap-3 rounded-xl p-2">
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#d6ad60] bg-[#fdfaf3] text-[#9a6b12]"
        aria-hidden="true"
      >
        <AirportPlaneIcon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400">
          {isToAirport ? 'Drop-off' : 'Pickup'}
        </span>
        <span className="mt-1 block truncate text-sm font-medium text-gray-900">{airportLabel}</span>
      </span>
      <span className="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500">
        Airport
      </span>
    </div>
  );

  return (
    <section className="rounded-2xl border border-[#ead7c1] bg-[#fdfaf3] p-3 shadow-sm">
      {isToAirport ? renderEditableEndpoint() : renderAirportEndpoint()}
      <div className="flex items-center gap-3 py-1 pl-[1.13rem]">
        <span className="h-7 border-l-2 border-dashed border-[#d6ad60]" aria-hidden="true" />
      </div>
      {isToAirport ? renderAirportEndpoint() : renderEditableEndpoint()}
    </section>
  );
}

